import type { WheelPickerOption } from '../wheel-picker';

/**
 * Returns a new Date set to 00:00 in the same timezone as the input.
 */
export const startOfDay = (date: Date): Date => {
	const d = new Date(date);
	d.setHours(0, 0, 0, 0);
	return d;
};

/**
 * ISO representation of startOfDay – convenient for serialisation / keys.
 */
export const startOfDayIso = (date: Date): string =>
	startOfDay(date).toISOString();

/**
 * Checks whether two dates fall on the same calendar day.
 */
export const isSameDay = (
	a: Date | undefined,
	b: Date | undefined,
): boolean => {
	if (!a || !b) return false;
	return startOfDay(a).getTime() === startOfDay(b).getTime();
};

export const addDays = (base: Date, delta: number): Date => {
	const d = new Date(base);
	d.setDate(d.getDate() + delta);
	return d;
};

export const formatCompositeDateLabel = (
	date: Date,
	locale?: string,
): string => {
	const day = date.getDate().toString().padStart(2, '0');
	const month = date.toLocaleString(locale, { month: 'short' });
	const weekday = date.toLocaleDateString(locale, { weekday: 'short' });
	return `${day} ${month}, ${weekday}`;
};

export const buildDateWindowOptions = (
	anchorDate: Date,
	windowHalf: number,
	locale?: string,
): WheelPickerOption[] => {
	const options: WheelPickerOption[] = [];
	for (let offset = -windowHalf; offset <= windowHalf; offset += 1) {
		const d = addDays(anchorDate, offset);
		const label = formatCompositeDateLabel(d, locale);
		options.push({ label, value: d.toISOString() });
	}
	return options;
};

export const buildDateRangeOptions = (
	minDate: Date,
	maxDate: Date,
	locale?: string,
): WheelPickerOption[] => {
	const options: WheelPickerOption[] = [];
	const start = new Date(startOfDayIso(minDate));
	const end = new Date(startOfDayIso(maxDate));
	for (let d = start; d <= end; d = addDays(d, 1)) {
		options.push({
			label: formatCompositeDateLabel(d, locale),
			value: startOfDayIso(d),
		});
	}
	return options;
};

export const buildHourOptions = (use24Hour: boolean): WheelPickerOption[] => {
	const count = use24Hour ? 24 : 12;
	const start = use24Hour ? 0 : 1;
	return Array.from({ length: count }, (_, i) => {
		const value = start + i;
		return {
			label: value.toString().padStart(2, '0'),
			value: value.toString(),
		};
	});
};

export const buildMinuteOptions = (step: number): WheelPickerOption[] => {
	const total = Math.ceil(60 / step);
	return Array.from({ length: total }, (_, i) => {
		const value = i * step;
		return {
			label: value.toString().padStart(2, '0'),
			value: value.toString(),
		};
	});
};

export const buildMeridiemOptions = (): WheelPickerOption[] => [
	{ label: 'AM', value: 'AM' },
	{ label: 'PM', value: 'PM' },
];

export const getDaysInMonth = (
	year: number,
	monthIndexZeroBased: number,
): number => {
	return new Date(year, monthIndexZeroBased + 1, 0).getDate();
};

export const buildDayOptions = (daysInMonth: number): WheelPickerOption[] => {
	return Array.from({ length: daysInMonth }, (_, i) => {
		const day = i + 1;
		return { label: day.toString(), value: day.toString() };
	});
};

export const buildMonthOptions = (locale?: string): WheelPickerOption[] => {
	return Array.from({ length: 12 }, (_, i) => {
		const date = new Date(2000, i, 1);
		const label = date.toLocaleString(locale, { month: 'long' });
		return { label, value: (i + 1).toString() };
	});
};

export const buildYearOptions = (
	minYear: number,
	maxYear: number,
): WheelPickerOption[] => {
	const options: WheelPickerOption[] = [];
	for (let y = minYear; y <= maxYear; y += 1) {
		options.push({ label: y.toString(), value: y.toString() });
	}
	return options;
};

export const combineDateIsoAndTime = (
	dateIso: string,
	timeHHmm: string,
): string => {
	const [h, m] = timeHHmm.split(':').map(Number);
	const d = new Date(dateIso);
	d.setHours(h || 0, m || 0, 0, 0);
	return d.toISOString();
};

export const parseDateInput = (input?: string | Date): Date => {
	if (input instanceof Date) return input;
	if (typeof input === 'string') return new Date(input);
	return new Date();
};

export const clampDate = (
	date: Date,
	min?: string | Date,
	max?: string | Date,
): Date => {
	const d = new Date(date);
	if (min) {
		const mn = parseDateInput(min);
		if (!Number.isNaN(mn.getTime()) && d < mn) return mn;
	}
	if (max) {
		const mx = parseDateInput(max);
		if (!Number.isNaN(mx.getTime()) && d > mx) return mx;
	}
	return d;
};

export const clampTimeHHmm = (
	value: string,
	min?: string,
	max?: string,
): string => {
	const toMinutes = (hhmm: string) => {
		const [h, m] = hhmm.split(':').map(Number);
		return (h || 0) * 60 + (m || 0);
	};
	const toHHmm = (mins: number) => {
		const h = Math.floor(mins / 60);
		const m = mins % 60;
		return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
	};
	let v = toMinutes(value);
	if (min) v = Math.max(v, toMinutes(min));
	if (max) v = Math.min(v, toMinutes(max));
	return toHHmm(v);
};

/**
 * Checks if a time (HH:mm format) is within the specified min/max range.
 * Uses smart defaults: min defaults to "00:00", max defaults to "23:59".
 * Handles overnight ranges when min > max.
 *
 * @param time - Time to check in HH:mm format (24-hour)
 * @param min - Optional minimum time in HH:mm format (24-hour). Defaults to "00:00"
 * @param max - Optional maximum time in HH:mm format (24-hour). Defaults to "23:59"
 * @returns true if time is within range, false if it violates bounds
 *
 * @example
 * checkTimeInRange("12:00", "10:00") // true - 12:00 is in range [10:00, 23:59]
 * checkTimeInRange("01:00", "23:00") // false - 01:00 is not in range [23:00, 23:59]
 * checkTimeInRange("23:30", "23:00") // true - 23:30 is in range [23:00, 23:59]
 * checkTimeInRange("01:00", undefined, "06:00") // true - 01:00 is in range [00:00, 06:00]
 * checkTimeInRange("01:00", "22:00", "06:00") // true - overnight range [22:00, 06:00]
 */
export const checkTimeInRange = (
	time: string,
	min?: string,
	max?: string,
): boolean => {
	const toMinutes = (hhmm: string) => {
		const [h, m] = hhmm.split(':').map(Number);
		return (h || 0) * 60 + (m || 0);
	};

	// No bounds set - always in range
	if (!min && !max) return true;

	// Set defaults for missing bounds to complete the range
	const effectiveMin = min || '00:00'; // Start of day if not specified
	const effectiveMax = max || '23:59'; // End of day if not specified

	const timeMinutes = toMinutes(time);
	const minMinutes = toMinutes(effectiveMin);
	const maxMinutes = toMinutes(effectiveMax);

	// Unified range validation logic
	// Same-day range: min <= max
	if (minMinutes <= maxMinutes) {
		return timeMinutes >= minMinutes && timeMinutes <= maxMinutes;
	}

	// Overnight range: min > max (crosses midnight)
	// Valid if time >= min (evening) OR time <= max (early morning)
	return timeMinutes >= minMinutes || timeMinutes <= maxMinutes;
};

const pad = (n: number) => String(n).padStart(2, '0');

/**
 * Computes a default HH:mm value when no explicit default is supplied.
 *
 * 1. Uses the current time, rounded down to the nearest `step` minutes.
 * 2. If that value violates the provided bounds, clamps to the closest valid
 *    edge (`min` preferred, else `max`).
 * 3. Falls back to "00:00" only if no other value is valid.
 */
export const getDefaultTime = (
	step: number,
	min?: string,
	max?: string,
): string => {
	// 1. Current time, rounded down to nearest step
	const now = new Date();
	const roundedMinutes = Math.floor(now.getMinutes() / step) * step;
	const candidate = `${pad(now.getHours())}:${pad(roundedMinutes)}`;

	// 2. Validate or clamp
	if (checkTimeInRange(candidate, min, max)) return candidate;
	if (min && checkTimeInRange(min, min, max)) return min;
	if (max && checkTimeInRange(max, min, max)) return max;

	// 3. Safe fallback
	return '00:00';
};

/**
 * Computes a sensible default Date (start-of-day) within optional min/max bounds.
 *
 * 1. Start with today (at 00:00).
 * 2. Clamp it to the provided bounds.
 */
export const getDefaultDate = (
	min?: string | Date,
	max?: string | Date,
): Date => {
	const todayStart = new Date(startOfDayIso(new Date()));
	return clampDate(todayStart, min, max);
};

/**
 * Returns HH:mm (24h) from a Date.
 */
export const formatHHmm = (d: Date): string => {
	return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/**
 * Clamps a Date to given min/max bounds (inclusive).
 */
export const clampDateTime = (
	d: Date,
	min?: string | Date,
	max?: string | Date,
): Date => {
	let result = d;
	if (min) {
		const mn = parseDateInput(min);
		if (result < mn) result = mn;
	}
	if (max) {
		const mx = parseDateInput(max);
		if (result > mx) result = mx;
	}
	return result;
};

/**
 * For a given selected date and overall min/max bounds, compute the time bounds
 * that apply only on that day (if any).
 * Returns HH:mm strings or undefined.
 */
export const computeTimeBounds = (
	selected: Date,
	min?: string | Date,
	max?: string | Date,
): { timeMin?: string; timeMax?: string } => {
	const minObj = min ? (min instanceof Date ? min : new Date(min)) : undefined;
	const maxObj = max ? (max instanceof Date ? max : new Date(max)) : undefined;

	const timeMin =
		minObj && isSameDay(minObj, selected) ? formatHHmm(minObj) : undefined;
	const timeMax =
		maxObj && isSameDay(maxObj, selected) ? formatHHmm(maxObj) : undefined;

	return { timeMin, timeMax };
};
